import React, { Fragment, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import Button, { type ButtonProps } from './Button';
import { type IChildAccount } from '../../interfaces/IChildAccount';
import { useToken } from '../../hooks/useToken';
import { useParams } from 'react-router-dom';
import { useAppDispatch } from '../../hooks';
import { fetchEvent } from '../../reducers/eventsSlice';
import { getConfig } from '../../config/config-helper';
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from '@headlessui/react';
import { ArrowPathIcon } from '@heroicons/react/20/solid';

interface ReconfigureAccountButtonProps {
  account?: IChildAccount;
  accountIds?: number[];
  eventSlug?: string;
  className?: string;
  size?: ButtonProps['size'];
  label?: string;
  intent?: ButtonProps['intent'];
  showConfirmation?: boolean;
  canUpdate?: boolean;
}

const ReconfigureAccountButton: React.FC<ReconfigureAccountButtonProps> = ({
  account,
  accountIds,
  eventSlug,
  className = '',
  size = 'small',
  label = 'Update account',
  intent = 'secondary',
  showConfirmation = false,
  canUpdate = true,
}) => {
  const [isReconfiguring, setIsReconfiguring] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const token = useToken();
  const { eventSlug: eventSlugFromParams } = useParams();
  const finalEventSlug = eventSlug ?? eventSlugFromParams;
  const dispatch = useAppDispatch();

  const getAccountIds = (): number[] => {
    if (accountIds && accountIds.length > 0) {
      return accountIds;
    } else if (account?.id) {
      return [account.id];
    }
    return [];
  };
  const handleReconfigureAccount = async () => {
    if (!finalEventSlug) {
      console.error('Event slug is required');
      return;
    }

    const { dataopsAdminAppApiEndpoint } = getConfig();
    const url = `${dataopsAdminAppApiEndpoint}/event_management/${finalEventSlug}/rerun_configure_pipeline`;
    const ids = getAccountIds();
    if (ids.length === 0 || !canUpdate) return;

    if (showConfirmation && !showConfirmDialog) {
      setShowConfirmDialog(true);
      return;
    }

    setIsReconfiguring(true);
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ child_account_ids: ids }),
      });

      if (!response.ok) {
        throw new Error('Failed to rerun configure pipeline');
      }

      await dispatch(fetchEvent({ token, slug: finalEventSlug }));
    } catch (error) {
      console.error('Error reconfiguring account:', error);
    } finally {
      setIsReconfiguring(false);
      setShowConfirmDialog(false);
    }
  };

  const isDisabled = () => {
    if (accountIds && accountIds.length > 0) {
      return !canUpdate;
    } else if (account) {
      return !canUpdate;
    }
    return true;
  };

  return (
    <>
      <Button
        size={size}
        intent={intent}
        className={`flex items-center gap-1 ${className}`}
        onClick={handleReconfigureAccount}
        loading={isReconfiguring}
        disabled={isDisabled()}
      >
        <ArrowPathIcon className={size === 'small' ? 'h-3 w-3 mr-1' : 'h-4 w-4 mr-1'} />
        <span>{label}</span>
      </Button>

      {/* Confirmation Dialog */}
      <Transition show={showConfirmDialog} as={Fragment}>
        <Dialog as="div" className="relative z-30" onClose={setShowConfirmDialog}>
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </TransitionChild>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <TransitionChild
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <DialogPanel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                  <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                    <div className="sm:flex sm:items-start">
                      <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                        <DialogTitle as="h3" className="text-base font-semibold leading-6 text-gray-900">
                          Confirm Update
                        </DialogTitle>
                        <div className="absolute right-0 top-0 pr-4 pt-4">
                          <button
                            type="button"
                            className="rounded-md bg-white text-gray-400 hover:text-gray-500"
                            onClick={() => {
                              setShowConfirmDialog(false);
                            }}
                          >
                            <span className="sr-only">Close</span>
                            <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                          </button>
                        </div>
                        <div className="mt-2">
                          <p className="text-sm text-gray-500">
                            You are about to update <span className="font-semibold">{getAccountIds().length}</span>{' '}
                            accounts. Are you sure you want to proceed?
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6  gap-4">
                    <Button
                      size="large"
                      intent="primary"
                      className={`flex items-center ${className}`}
                      onClick={handleReconfigureAccount}
                      disabled={false}
                    >
                      Confirm
                    </Button>
                    <Button
                      size="large"
                      intent="light"
                      className={`flex items-center ${className}`}
                      onClick={() => {
                        setShowConfirmDialog(false);
                      }}
                      disabled={false}
                    >
                      Cancel
                    </Button>
                  </div>
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};

export default ReconfigureAccountButton;
